// The read and write endpoints the workload pages use beyond the console-wide lists in
// api/queries.ts: one environment, workflow or network, an environment's workspaces and a
// workspace's repositories, a workflow's runs, and the run and environment actions.
import { queryOptions } from "@tanstack/react-query";

import { api, type Environment, type Run, type Schemas } from "../api/client";
import { unwrap } from "../api/queries";

export type Network = Schemas["NetworkDto"];
export type Repository = Schemas["RepositoryDto"];

export const environmentQuery = (envId: string) =>
  queryOptions({
    queryKey: ["environments", envId],
    queryFn: () => unwrap(api.GET("/api/environments/{env_id}", { params: { path: { env_id: envId } } })),
  });

export const environmentWorkspacesQuery = (envId: string) =>
  queryOptions({
    queryKey: ["environments", envId, "workspaces"],
    queryFn: () => unwrap(api.GET("/api/environments/{env_id}/workspaces", { params: { path: { env_id: envId } } })),
  });

export const repositoriesQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "repositories"],
    queryFn: () =>
      unwrap(api.GET("/api/workspaces/{workspace_id}/repositories", { params: { path: { workspace_id: workspaceId } } })),
  });

export const networkQuery = (networkId: string) =>
  queryOptions({
    queryKey: ["networks", networkId],
    queryFn: () => unwrap(api.GET("/api/networks/{network_id}", { params: { path: { network_id: networkId } } })),
  });

export const workflowQuery = (workflowId: string) =>
  queryOptions({
    queryKey: ["workflows", workflowId],
    queryFn: () => unwrap(api.GET("/api/workflows/{workflow_id}", { params: { path: { workflow_id: workflowId } } })),
  });

/** Newest first; the server's default page (see `listRuns` in api/queries.ts for `limit`). */
export const workflowRunsQuery = (workflowId: string) =>
  queryOptions({
    queryKey: ["workflows", workflowId, "runs"],
    queryFn: () =>
      unwrap(api.GET("/api/workflows/{workflow_id}/runs", { params: { path: { workflow_id: workflowId, limit: null } } })),
  });

export const triggerRun = (workflowId: string): Promise<Run> =>
  unwrap(api.POST("/api/workflows/{workflow_id}/runs", { params: { path: { workflow_id: workflowId } } }));

export const cancelRun = (runId: string): Promise<Run> =>
  unwrap(api.POST("/api/runs/{run_id}/cancel", { params: { path: { run_id: runId } }, body: { reason: "cancelled from the web console" } }));

export type EnvironmentAction = "stop" | "start" | "retry";

export const environmentAction = (envId: string, action: EnvironmentAction): Promise<Environment> =>
  unwrap(api.POST("/api/environments/{env_id}/actions", { params: { path: { env_id: envId } }, body: { action } }));
